/**
 * Socket Service for Real-time Order Notifications and Session Management
 * Manages WebSocket connection and event handling for order notifications and force logout
 */

import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3600';

class SocketService {
    constructor() {
        this.socket = null;
        this.isConnected = false;
        this.listeners = new Map();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.currentBranchId = null;
        this.currentSessionId = null;
        this.forceLogoutCallback = null;
    }

    /**
     * Set the callback function for force logout events
     * @param {Function} callback - Callback to execute on force logout
     */
    setForceLogoutCallback(callback) {
        this.forceLogoutCallback = callback;
    }

    /**
     * Initialize socket connection
     * @param {Object} userData - User data for room subscription
     * @param {string} sessionId - Current session ID
     */
    connect(userData, sessionId = null) {
        if (this.socket?.connected) {
            console.log('[SocketService] Already connected');
            // If already connected, just join the user room for session management
            if (sessionId && userData?._id) {
                this.joinUserRoom(userData._id, sessionId);
            }
            return;
        }

        this.currentSessionId = sessionId;
        console.log('[SocketService] Connecting to:', SOCKET_URL);

        this.socket = io(SOCKET_URL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: this.maxReconnectAttempts,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000
        });

        this.setupEventListeners(userData, sessionId);
    }

    /**
     * Join user-specific room for force logout events
     * @param {string} userId - User ID
     * @param {string} sessionId - Session ID
     */
    joinUserRoom(userId, sessionId) {
        if (!this.socket?.connected || !userId) return;
        
        this.currentSessionId = sessionId;
        this.socket.emit('joinUserRoom', { userId, sessionId });
        console.log('[SocketService] Joined user room for session management');
    }

    /**
     * Leave user-specific room
     * @param {string} userId - User ID
     */
    leaveUserRoom(userId) {
        if (!this.socket?.connected || !userId) return;
        
        this.socket.emit('leaveUserRoom', { userId });
        console.log('[SocketService] Left user room');
    }

    /**
     * Setup socket event listeners
     * @param {Object} userData - User data for room subscription
     * @param {string} sessionId - Current session ID
     */
    setupEventListeners(userData, sessionId) {
        if (!this.socket) return;

        this.socket.on('connect', () => {
            console.log('[SocketService] Connected, socket id:', this.socket.id);
            this.isConnected = true;
            this.reconnectAttempts = 0;
            
            // Join user room for force logout events
            if (userData?._id && sessionId) {
                this.joinUserRoom(userData._id, sessionId);
            }
            
            // Join notification rooms based on user role
            this.joinNotificationRooms(userData);
        });

        this.socket.on('disconnect', (reason) => {
            console.log('[SocketService] Disconnected:', reason);
            this.isConnected = false;
        });

        this.socket.on('connect_error', (error) => {
            console.log('[SocketService] Connection error:', error.message);
            this.reconnectAttempts++;
        });

        this.socket.on('reconnect', (attemptNumber) => {
            console.log('[SocketService] Reconnected after', attemptNumber, 'attempts');
            this.isConnected = true;
            // Re-join rooms after reconnect
            if (userData?._id && sessionId) {
                this.joinUserRoom(userData._id, sessionId);
            }
            this.joinNotificationRooms(userData);
        });

        // Listen for force logout events
        this.socket.on('forceLogout', (data) => {
            console.log('[SocketService] Force logout received:', data);
            
            // Check if this force logout is for the current session
            if (data.sessionId === this.currentSessionId) {
                console.log('[SocketService] Force logout is for this session, executing callback');
                if (this.forceLogoutCallback) {
                    this.forceLogoutCallback(data);
                }
                this.notifyListeners('forceLogout', data);
            } else {
                console.log('[SocketService] Force logout is for a different session, ignoring');
            }
        });

        // Listen for order notifications
        this.socket.on('orderNotification', (data) => {
            console.log('[SocketService] Order notification received:', data);
            this.notifyListeners('orderNotification', data);
        });

        // Listen for delivery notifications (driver status changes)
        this.socket.on('deliveryNotification', (data) => {
            console.log('[SocketService] Delivery notification received:', data);
            this.notifyListeners('orderNotification', data); // Reuse same listener to trigger order refresh
        });

        // Live customer app presence (Micro Analysis — Today)
        this.socket.on('customerPresenceUpdate', (data) => {
            this.notifyListeners('customerPresenceUpdate', data);
        });
    }

    /**
     * Join notification rooms based on user role and permissions
     * @param {Object} userData - User data
     */
    joinNotificationRooms(userData) {
        if (!this.socket?.connected || !userData) return;

        const { _id, masterId, selectedBranch, role, supportAdminAccess, masterAccount } = userData;

        const roomData = {
            userId: _id,
            masterId: role === 1 ? selectedBranch : masterId,
            branchId: _id,
            role,
            supportAdminAccess,
            masterAccount,
            selectedBranchId: this.currentBranchId || selectedBranch
        };

        console.log('[SocketService] Joining rooms with data:', roomData);
        this.socket.emit('joinNotificationRooms', roomData);
    }

    /**
     * Change branch subscription when user selects a different branch
     * @param {string} previousBranchId - Previous branch ID
     * @param {string} newBranchId - New branch ID
     * @param {string} masterId - Master ID
     * @param {boolean} viewAllBranches - Whether viewing all branches
     */
    changeBranchSubscription(previousBranchId, newBranchId, masterId, viewAllBranches = false) {
        if (!this.socket?.connected) return;

        this.currentBranchId = newBranchId;

        console.log('[SocketService] Changing branch subscription:', {
            previousBranchId,
            newBranchId,
            viewAllBranches
        });

        this.socket.emit('changeBranchSubscription', {
            previousBranchId,
            newBranchId,
            masterId,
            viewAllBranches
        });
    }

    /**
     * Subscribe to real-time customer app presence for a store master (Today tab).
     */
    joinCustomerPresenceAnalytics(masterId) {
        if (!this.socket?.connected || !masterId) return;
        this.socket.emit('joinCustomerPresenceAnalytics', { masterId });
    }

    leaveCustomerPresenceAnalytics() {
        if (!this.socket?.connected) return;
        this.socket.emit('leaveCustomerPresenceAnalytics');
    }

    /**
     * Add event listener for order notifications
     * @param {string} event - Event name
     * @param {Function} callback - Callback function
     * @returns {Function} - Unsubscribe function
     */
    addListener(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(callback);

        // Return unsubscribe function
        return () => {
            this.listeners.get(event)?.delete(callback);
        };
    }

    /**
     * Notify all listeners of an event
     * @param {string} event - Event name
     * @param {Object} data - Event data
     */
    notifyListeners(event, data) {
        const eventListeners = this.listeners.get(event);
        if (eventListeners) {
            eventListeners.forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error('[SocketService] Listener error:', error);
                }
            });
        }
    }

    /**
     * Disconnect socket
     */
    disconnect() {
        if (this.socket) {
            this.socket.emit('leaveNotificationRooms');
            this.socket.disconnect();
            this.socket = null;
            this.isConnected = false;
            this.listeners.clear();
            console.log('[SocketService] Disconnected');
        }
    }

    /**
     * Check if socket is connected
     * @returns {boolean}
     */
    isSocketConnected() {
        return this.isConnected && this.socket?.connected;
    }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
